<div
  class="eventq-actionable-list"
  [class.eventq-actionable-list--disabled]="isInteractionDisabled()"
>
  @if (resolvedShowSearch()) {
    <div class="eventq-actionable-list__search">
      <input
        class="eventq-actionable-list__search-input"
        type="search"
        [placeholder]="searchPlaceholder()"
        [value]="searchQuery()"
        [attr.aria-label]="searchPlaceholder()"
        [disabled]="loading() || isInteractionDisabled()"
        (input)="onSearchInput($event)"
      />
      <mat-icon class="eventq-actionable-list__search-icon" aria-hidden="true">search</mat-icon>
    </div>
  }

  @if (loading()) {
    @if (appearance() === 'tiles') {
      <div class="eventq-actionable-list__tiles eventq-actionable-list__tiles--skeleton" aria-hidden="true">
        @for (index of skeletonIndexes(); track index) {
          <div class="eventq-actionable-list__skeleton-tile">
            <div class="eventq-actionable-list__skeleton-block eventq-actionable-list__skeleton-block--label"></div>
            <div class="eventq-actionable-list__skeleton-block eventq-actionable-list__skeleton-block--button"></div>
          </div>
        }
      </div>
    } @else {
      <div class="eventq-actionable-list__skeleton" aria-hidden="true">
        @for (index of skeletonIndexes(); track index) {
          <div class="eventq-actionable-list__skeleton-row">
            <div class="eventq-actionable-list__skeleton-block eventq-actionable-list__skeleton-block--icon"></div>
            <div class="eventq-actionable-list__skeleton-block eventq-actionable-list__skeleton-block--text"></div>
            <div class="eventq-actionable-list__skeleton-block eventq-actionable-list__skeleton-block--button"></div>
          </div>
        }
      </div>
    }
  } @else if (items().length === 0) {
    <p class="eventq-actionable-list__empty">{{ emptyMessage() }}</p>
  } @else if (appearance() === 'tiles') {
    <div class="eventq-actionable-list__tiles" role="list" [attr.aria-label]="listAriaLabel()">
      @for (item of items(); track item.id) {
        <div class="eventq-actionable-list__tile" role="listitem">
          <div class="eventq-actionable-list__tile-label">
            @for (line of itemLabelLines(item); track $index) {
              <span class="eventq-actionable-list__tile-line">{{ line }}</span>
            }
          </div>
          <div class="eventq-actionable-list__tile-action">
            <button
              type="button"
              class="eventq-actionable-list__activate"
              [class.eventq-actionable-list__activate--disabled]="!!activateDisabledById()[item.id]"
              [disabled]="!!activateDisabledById()[item.id]"
              (click)="onActivate(item, $event)"
            >
              {{ activateLabel() }}
            </button>
          </div>
        </div>
      }
    </div>
  } @else {
    <div
      class="eventq-actionable-list__viewport"
      [class.eventq-actionable-list__viewport--sized]="!!listHeight()"
      [style]="scrollStyle()"
    >
    <eventq-scroll-area
      class="eventq-actionable-list__scroll"
      axis="vertical"
      surface="deep"
    >
      <ul class="eventq-actionable-list__list" role="list" [attr.aria-label]="listAriaLabel()">
        @for (item of items(); track item.id) {
          <li class="eventq-actionable-list__row">
            @if (item.icon) {
              <mat-icon class="eventq-actionable-list__icon" aria-hidden="true">{{ item.icon }}</mat-icon>
            }
            <div class="eventq-actionable-list__main">
              <span class="eventq-actionable-list__label">{{ item.label ?? itemLabelLines(item)[0] }}</span>
              @if (item.showInfo) {
                <button
                  type="button"
                  class="eventq-actionable-list__info"
                  aria-label="Item info"
                  [disabled]="isInteractionDisabled()"
                  (click)="onInfo(item, $event)"
                  (mouseenter)="onInfoEnter(item, $event)"
                  (mouseleave)="onInfoLeave(item)"
                  (focus)="onInfoEnter(item, $event)"
                  (blur)="onInfoLeave(item)"
                >
                  <mat-icon aria-hidden="true">info_outline</mat-icon>
                </button>
              }
            </div>
            @if (showStatus(item)) {
              <span
                class="eventq-actionable-list__status"
                [class.eventq-actionable-list__status--activated]="statusTone(item.status) === 'activated'"
                [class.eventq-actionable-list__status--failed]="statusTone(item.status) === 'failed'"
              >
                {{ item.statusLabel }}
              </span>
            }
            <button
              type="button"
              class="eventq-actionable-list__activate"
              [class.eventq-actionable-list__activate--menu]="usesActivateMenu()"
              [class.eventq-actionable-list__activate--disabled]="!!activateDisabledById()[item.id]"
              [attr.aria-expanded]="
                overlay()?.kind === 'activate' && overlay()?.itemId === item.id
              "
              [attr.aria-disabled]="activateDisabledById()[item.id] ? 'true' : null"
              [disabled]="!!activateDisabledById()[item.id]"
              (click)="onActivate(item, $event)"
            >
              <span>{{ activateLabel() }}</span>
              @if (usesActivateMenu()) {
                <mat-icon
                  class="eventq-actionable-list__activate-chevron"
                  data-testid="activate-chevron"
                  aria-hidden="true"
                >
                  expand_more
                </mat-icon>
              }
            </button>
          </li>
        }
      </ul>
    </eventq-scroll-area>
    </div>
  }
</div>

@if (overlay(); as state) {
  @if (state.kind === 'activate') {
    <div
      #overlayPanel
      class="eventq-actionable-list__activate-menu"
      data-testid="activate-options-menu"
      role="menu"
      [style.top]="overlayStyle()?.top"
      [style.left]="overlayStyle()?.left"
      [style.background]="'#ffe0b2'"
    >
      @for (option of overlayActivateOptions(); track option.id) {
        <button
          type="button"
          class="eventq-actionable-list__activate-option"
          data-testid="activate-option"
          role="menuitem"
          [attr.data-option-id]="option.id"
          (click)="onActivateOption(option.id, $event)"
        >
          {{ option.label }}
        </button>
      }
    </div>
  } @else {
    <div
      #overlayPanel
      class="eventq-actionable-list__info-overlay"
      data-testid="actionable-list-info-overlay"
      role="tooltip"
      [style.top]="overlayStyle()?.top"
      [style.left]="overlayStyle()?.left"
      [style.background]="'#424242'"
    >
      @for (line of state.lines; track $index) {
        <p class="eventq-actionable-list__info-line">{{ line }}</p>
      }
    </div>
  }
}
